// Canonical persistence fields. Legacy names exist only at the UI boundary.
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
export const problemIds = (record = {}) => [...new Set(
  (record.problem_card_ids ?? record.problem_ids ?? (record.problem_id ? [record.problem_id] : []))
    .filter(Boolean).map(String)
)];
export const horizon = (value) => ({ now: 'Now', next: 'Next', later: 'Later' }[String(value || '').toLowerCase()] || 'Next');

export function normalizeRecord(table, record) {
  if (!record || typeof record !== 'object') return record;
  const r = { ...record, workspace_id: record.workspace_id || record.user_id };
  if (table === 'roadmap_items') {
    r.problem_card_ids = problemIds(r);
    r.problem_id = r.problem_card_ids[0] || '';
    r.okr_id = r.okr_id || r.okr_ref || null;
    r.okr_ref = r.okr_id || '';
    r.time_horizon = horizon(r.time_horizon);
    r.progress = Number(r.progress ?? r.progress_percentage ?? 0);
    r.status = r.status === 'planning' ? 'planned' : (r.status || 'planned');
    r.impact = Number(r.impact ?? (Number(r.expected_impact) || 0));
  }
  if (table === 'problem_cards') {
    r.okr_id = r.okr_id || r.okr_ref || null;
    r.okr_ref = r.okr_id || '';
    r.customer_segment = r.target_segment || r.customer_segment || '';
    r.time_horizon = horizon(r.time_horizon);
    r.status = String(r.status).toLowerCase() === 'discovery' ? 'discovered' : r.status;
  }
  if (table === 'evidence') {
    r.problem_card_id = r.problem_card_id || r.problem_id;
    r.relationship = ['supports', 'contradicts', 'neutral'].includes(r.relationship) ? r.relationship : 'unreviewed';
    r.problem_id = r.problem_card_id;
    r.url = r.source_url ?? r.url ?? '';
    r.note = r.description ?? r.note ?? '';
    r.dateISO = r.observed_at || r.dateISO || r.created_at;
    r.strength = Number(r.strength || 3);
  }
  if (table === 'issues') {
    r.tier = Number(r.tier) || ({ tier1: 1, tier2: 2, noise: 3, tier3: 3 }[r.tier] || 3);
    r.summary = r.description || r.summary || '';
    r.tags = r.labels || r.tags || [];
    r.owner = r.assignee || r.owner || '';
    r.ext_key = r.external_id || r.ext_key || '';
  }
  return r;
}

export function prepareRecord(table, input) {
  const r = { ...input };
  const rename = (old, current, convert = v => v) => {
    if (own(r, old)) { if (!own(r, current)) r[current] = convert(r[old]); delete r[old]; }
  };
  if (['problem_cards', 'roadmap_items'].includes(table)) {
    rename('okr_ref', 'okr_id', v => v || null);
    if (own(r, 'okr_id')) r.okr_id = r.okr_id || null;
    if (own(r, 'time_horizon')) r.time_horizon = horizon(r.time_horizon);
  }
  if (table === 'roadmap_items') {
    if (['problem_id', 'problem_ids', 'problem_card_ids'].some(key => own(r, key))) r.problem_card_ids = problemIds(r);
    delete r.problem_id; delete r.problem_ids;
    rename('progress_percentage', 'progress');
    if (own(r, 'target_date')) r.target_date = r.target_date || null;
    if (r.status === 'planning') r.status = 'planned';
  }
  if (table === 'problem_cards') {
    rename('customer_segment', 'target_segment');
    if (String(r.status).toLowerCase() === 'discovery') r.status = 'discovered';
  }
  if (table === 'evidence') {
    rename('problem_id', 'problem_card_id'); rename('url', 'source_url');
    rename('note', 'description'); rename('dateISO', 'observed_at', v => v || null);
    if (!r.title && (r.description || r.source_url)) r.title = (r.description || r.source_url).slice(0, 160);
  }
  if (table === 'issues') {
    rename('summary', 'description'); rename('tags', 'labels'); rename('owner', 'assignee');
    rename('ext_key', 'external_id'); rename('url', 'external_url');
    if (own(r, 'tier')) r.tier = normalizeRecord(table, r).tier;
  }
  return r;
}

export const V2_TABLES = ['workspaces', 'workspace_members', 'decisions', 'experiments', 'experiment_results', 'learnings', 'inbox_items', 'insight_feedback', 'source_records', 'workspace_insights'];

export const REFERENCE_FIELDS = {
  problem_cards: { okr_id:'okrs' },
  roadmap_items: { problem_card_ids:'problem_cards', okr_id:'okrs' },
  evidence: { problem_card_id:'problem_cards' },
  hypotheses: { problem_card_id:'problem_cards', roadmap_item_id:'roadmap_items' },
  decisions: { problem_card_id:'problem_cards', roadmap_item_id:'roadmap_items', okr_id:'okrs', evidence_ids:'evidence' },
  experiments: { hypothesis_id:'hypotheses' },
  experiment_results: { experiment_id:'experiments', evidence_ids:'evidence' },
  learnings: { result_id:'experiment_results', decision_id:'decisions' },
  reminders: { roadmap_item_id:'roadmap_items', okr_id:'okrs' },
  time_entries: { roadmap_item_id:'roadmap_items', okr_id:'okrs' },
};
